"use client";
import * as SelectPrimitive from "@radix-ui/react-select";
import { cn } from "@scott/ui/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { ChevronDown, ChevronUp } from "lucide-react";
import type * as React from "react";
import { Body } from "../body";
import { CloseIcon } from "../icon/icons/close-icon";
import { TickIcon } from "../icon/icons/tick-icon";

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

// Helper function to avoid nested ternaries
function getStatusTextColor(
  status?: "error" | "success" | "none" | null
): string {
  if (status === "error") {
    return "text-danger-medium";
  }
  if (status === "success") {
    return "text-success-medium";
  }
  return "text-neutral-dark";
}

const selectTriggerVariants = cva(
  "justify-left flex w-full flex-row items-center gap-2 rounded-none p-3 font-paragraph text-sm ring-offset-background",
  {
    variants: {
      status: {
        error: "border-danger-medium",
        success: "border-success-medium",
        none: "",
      },
      defaultVariants: {
        status: "none",
      },
    },
  }
);
export interface SelectTriggerProps
  extends VariantProps<typeof selectTriggerVariants>,
    SelectPrimitive.SelectTriggerProps {
  asChild?: boolean;
  icon?: React.ReactNode;
  message?: string | React.ReactNode;
}

const SelectTrigger = ({
  className,
  children,
  icon,
  status,
  message,
  ref,
  ...props
}: SelectTriggerProps & {
  ref?: React.RefObject<React.ElementRef<
    typeof SelectPrimitive.Trigger
  > | null>;
}) => (
  <div className="flex w-full flex-1 flex-col gap-1">
    <SelectPrimitive.Trigger
      className={cn(
        "disable:neutral-darker disable:cursor-not-allowed border border-neutral-medium disable:bg-neutral-medium text-neutral-darker disable:opacity-50 disable:shadow-none placeholder:text-muted-foreground hover:border-neutral-dark hover:bg-neutral-lightest focus:outline-none focus:ring-offset-2 focus-visible:border-neutral-dark focus-visible:bg-neutral-lightest focus-visible:ring-2 focus-visible:ring-brand-medium focus-visible:ring-offset-2 data-[state=open]:bg-neutral-lightest [&>span]:line-clamp-1",
        selectTriggerVariants({ status, className }),
        "[&[data-state=open]>svg]:rotate-180",
        props.disabled && "bg-neutral-medium"
      )}
      ref={ref}
      {...props}
    >
      <div className="flex w-[99%] flex-row gap-2">
        {icon && <div className={cn(getStatusTextColor(status))}>{icon}</div>}
        {children}
      </div>
      <SelectPrimitive.Icon asChild>
        <ChevronDown className={cn(getStatusTextColor(status))} />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>

    {status && (
      <div>
        {status === "error" && (
          <span className="flex items-center gap-1">
            <CloseIcon className="h-3 w-3 rounded-full text-error" size="sm" />
            <Body className="text-error" size="xs">
              {message}
            </Body>
          </span>
        )}
        {status === "success" && (
          <span className="flex items-center gap-1">
            <TickIcon className="h-3 w-3 text-button-success" size="sm" />
            <Body className="text-button-success" size="xs">
              {message}
            </Body>
          </span>
        )}
        {props.disabled && message && (
          <span className="flex gap-1 text-neutral-medium">
            <Body size="xs">{message}</Body>
          </span>
        )}
        {!(props.disabled || status) && message && (
          <span className="flex gap-1">
            <Body size="xs">{message}</Body>
          </span>
        )}
      </div>
    )}
  </div>
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton> & {
  ref?: React.RefObject<React.ElementRef<
    typeof SelectPrimitive.ScrollUpButton
  > | null>;
}) => (
  <SelectPrimitive.ScrollUpButton
    className={cn(
      "flex cursor-default items-start justify-center py-1",
      className
    )}
    ref={ref}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
);
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
const SelectScrollDownButton = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton> & {
  ref?: React.RefObject<React.ElementRef<
    typeof SelectPrimitive.ScrollDownButton
  > | null>;
}) => (
  <SelectPrimitive.ScrollDownButton
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    ref={ref}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
);
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;
const SelectContent = ({
  className,
  children,
  position = "popper",
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
  ref?: React.RefObject<React.ElementRef<
    typeof SelectPrimitive.Content
  > | null>;
}) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      className={cn(
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 min-w-[8rem] overflow-hidden border border-neutral-darker bg-background py-2 text-popover-foreground shadow-md data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=open]:bg-white",
        position === "popper" &&
          "data-[side=left]:-translate-x-1 data-[side=top]:-translate-y-1 data-[side=right]:translate-x-1 data-[side=bottom]:translate-y-1",
        className
      )}
      position={position}
      ref={ref}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
);
SelectContent.displayName = SelectPrimitive.Content.displayName;
const SelectLabel = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label> & {
  ref?: React.RefObject<React.ElementRef<typeof SelectPrimitive.Label> | null>;
}) => (
  <SelectPrimitive.Label
    className={cn("px-2 py-2 text-neutral-darkest text-sm", className)}
    ref={ref}
    {...props}
  />
);
SelectLabel.displayName = SelectPrimitive.Label.displayName;
const SelectItem = ({
  className,
  children,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
  ref?: React.RefObject<React.ElementRef<typeof SelectPrimitive.Item> | null>;
}) => (
  <SelectPrimitive.Item
    className={cn(
      "relative cursor-pointer select-none items-start p-2 text-sm outline-none hover:bg-neutral-lightest data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    ref={ref}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-start text-sm">
      <SelectPrimitive.ItemIndicator />
    </span>
    <SelectPrimitive.ItemText className="text-sm">
      {children}
    </SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
);
SelectItem.displayName = SelectPrimitive.Item.displayName;
const SelectSeparator = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator> & {
  ref?: React.RefObject<React.ElementRef<
    typeof SelectPrimitive.Separator
  > | null>;
}) => (
  <SelectPrimitive.Separator
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    ref={ref}
    {...props}
  />
);
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;
export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  selectTriggerVariants,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
